import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../app/layouts/MainLayout';
import { Button } from '../../../components/ui/Button';
import FullScreenModal from '../../../components/ui/Modal';
import ClassCatalog from '../components/ClassCatalog';
import ClassesFilters from '../components/ClassesFilters';
import ClassManagementPanel, { ClassFormValues } from '../components/ClassManagementPanel';
import { ClassRecord } from '../../trimesters/context/TrimestersContext';
import { ReportType } from '../../reports/types/report.types';
import { useSegments } from '../../trimesters/hooks';
import { useClassrooms } from '../hooks/useClassrooms';
import { useTeacherCandidates } from '../hooks/useTeacherCandidates';
import { useTrimesters } from '../../trimesters/context/TrimestersContext';

const emptyFormValues: ClassFormValues = {
  title: '',
  teacherMemberIds: [],
  gender: 'Mista',
  ageGroup: 'Adultos',
};

const buildFormValues = (item?: ClassRecord | null): ClassFormValues => ({
  title: item?.title ?? '',
  teacherMemberIds: item?.teacherMemberIds ?? [],
  gender: item?.gender ?? 'Mista',
  ageGroup:
    item?.category === 'Berçário'
      ? 'Berçário'
      : item?.category === 'Maternal'
        ? 'Maternal'
        : item?.category === 'Jardim de Infância'
          ? 'Jardim de Infância'
          : item?.category === 'Primários'
            ? 'Primários'
            : item?.category === 'Juniores'
              ? 'Juniores'
              : item?.category === 'Pré-Adolescentes'
                ? 'Pré-Adolescentes'
                : item?.category.includes('Adolescentes')
                  ? 'Adolescentes'
                  : item?.category.includes('Juvenis') || item?.category.includes('Jovens')
                    ? 'Juvenis/Jovens'
                    : item?.category ?? 'Adultos',
});

const resolveAgeGroupFilter = (category: string) => {
  if (category === 'Berçário') return 'Berçário';
  if (category === 'Maternal') return 'Maternal';
  if (category === 'Jardim de Infância') return 'Jardim de Infância';
  if (category === 'Primários') return 'Primários';
  if (category === 'Juniores') return 'Juniores';
  if (category === 'Pré-Adolescentes') return 'Pré-Adolescentes';
  if (category.includes('Adolescentes')) return 'Adolescentes';
  if (category.includes('Juvenis') || category.includes('Jovens')) return 'Juvenis/Jovens';
  return category;
};

const ClassesPage = () => {
  const navigate = useNavigate();
  const { segments } = useSegments();
  const { currentTrimesterId, setCurrentTrimesterId, trimesters } = useTrimesters();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeTrimesterId = searchParams.get('trimester_id') ?? '';
  const activeTrimesterId = routeTrimesterId || currentTrimesterId;
  const { classrooms, isLoading, error, createClassroom, updateClassroom, removeClassroom } = useClassrooms(activeTrimesterId);
  const { teacherCandidates, teachersError } = useTeacherCandidates();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [classToRemove, setClassToRemove] = useState<ClassRecord | null>(null);
  const [formValues, setFormValues] = useState<ClassFormValues>(emptyFormValues);
  const [formError, setFormError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const teacherOptions = teacherCandidates.map((teacher) => ({
    value: teacher.id,
    label: teacher.name,
    description: teacher.role,
  }));
  const segmentOptions = segments.map((segment) => ({
    value: segment.name,
    label: segment.name,
    description: `${segment.ageStart} a ${segment.ageEnd} anos`,
  }));

  const searchValue = searchParams.get('q') ?? '';
  const genderValue = searchParams.get('gender') ?? 'Todas';
  const ageGroupValue = searchParams.get('age_group') ?? 'Todas';

  useEffect(() => {
    if (!routeTrimesterId || routeTrimesterId === currentTrimesterId) {
      return;
    }

    const trimesterExists = trimesters.some((trimester) => trimester.id === routeTrimesterId);
    if (trimesterExists) {
      setCurrentTrimesterId(routeTrimesterId);
    }
  }, [currentTrimesterId, routeTrimesterId, setCurrentTrimesterId, trimesters]);

  const updateFilterParams = (updates: Record<string, string>) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === 'Todas' || value === '0') {
        nextParams.delete(key);
        return;
      }

      nextParams.set(key, value);
    });

    setSearchParams(nextParams);
  };

  const filteredClasses = useMemo(() => {
    return classrooms.filter((item) => {
      const normalizedTerm = searchValue.trim().toLowerCase();
      const matchesSearch =
        normalizedTerm.length === 0 ||
        item.title.toLowerCase().includes(normalizedTerm) ||
        item.teachers.some((teacher) => teacher.toLowerCase().includes(normalizedTerm)) ||
        item.category.toLowerCase().includes(normalizedTerm);
      const matchesGender = genderValue === 'Todas' || item.gender === genderValue;
      const matchesAgeGroup = ageGroupValue === 'Todas' || resolveAgeGroupFilter(item.category) === ageGroupValue;

      return matchesSearch && matchesGender && matchesAgeGroup;
    });
  }, [ageGroupValue, classrooms, genderValue, searchValue]);

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingClassId(null);
    setFormValues(emptyFormValues);
    setFormError(null);
  };

  const openCreateModal = () => {
    setEditingClassId(null);
    setFormValues(emptyFormValues);
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const handleSelectClass = (classId: string) => {
    navigate(`/classes/${classId}/licoes?trimester_id=${activeTrimesterId}`);
  };

  const handleOpenClassReport = (classId: string, reportType: ReportType) => {
    navigate(`/relatorios/${reportType}?class_id=${classId}&trimester_id=${activeTrimesterId}`);
  };

  const handleEditClass = (classId: string) => {
    const currentClass = classrooms.find((item) => item.id === classId);

    if (!currentClass) {
      return;
    }

    setEditingClassId(classId);
    setFormValues(buildFormValues(currentClass));
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const handleChangeFormValue = (field: keyof ClassFormValues, value: string | string[]) => {
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleSaveClass = async () => {
    const selectedTeacherIds = formValues.teacherMemberIds.filter((teacherId) => teacherId.trim().length > 0);
    const selectedTeacherNames = teacherCandidates
      .filter((teacher) => selectedTeacherIds.includes(teacher.id))
      .map((teacher) => teacher.name);
    const selectedSegment = segments.find((segment) => segment.name === formValues.ageGroup) ?? null;

    if (!formValues.title.trim()) {
      setFormError('Informe o nome da turma.');
      return;
    }

    try {
      if (editingClassId) {
        await updateClassroom(editingClassId, {
          title: formValues.title.trim(),
          teacherNames: selectedTeacherNames,
          teacherMemberIds: selectedTeacherIds,
          gender: formValues.gender,
          segmentId: selectedSegment?.id ?? null,
          trimesterId: activeTrimesterId,
        });
      } else {
        await createClassroom({
          title: formValues.title.trim(),
          teacherNames: selectedTeacherNames,
          teacherMemberIds: selectedTeacherIds,
          gender: formValues.gender,
          segmentId: selectedSegment?.id ?? null,
          trimesterId: activeTrimesterId,
        });
      }

      closeFormModal();
    } catch (saveError) {
      setFormError(saveError instanceof Error ? saveError.message : 'Nao foi possivel salvar a turma.');
    }
  };

  const handleRequestRemoveClass = (classId: string) => {
    const currentClass = classrooms.find((item) => item.id === classId) ?? null;
    setRemoveError(null);
    setClassToRemove(currentClass);
  };

  const handleConfirmRemoveClass = async () => {
    if (!classToRemove) {
      return;
    }

    try {
      await removeClassroom(classToRemove.id);
      setClassToRemove(null);
      setRemoveError(null);
    } catch (deleteError) {
      setRemoveError(deleteError instanceof Error ? deleteError.message : 'Nao foi possivel remover a turma.');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Turmas</h2>
            </div>
            <Button className="h-12 gap-2 px-4" onClick={openCreateModal}>
              <Plus size={18} />
              Nova turma
            </Button>
          </div>

          <ClassesFilters
            searchValue={searchValue}
            genderValue={genderValue}
            ageGroupValue={ageGroupValue}
            ageGroupOptions={['Todas', ...segments.map((segment) => segment.name)]}
            onSearchChange={(value) => updateFilterParams({ q: value })}
            onGenderChange={(value) => updateFilterParams({ gender: value })}
            onAgeGroupChange={(value) => updateFilterParams({ age_group: value })}
            onClearFilters={() => setSearchParams(new URLSearchParams())}
          />
        </section>

        {error && (
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-medium text-rose-800">
            {error}
          </div>
        )}

        {teachersError && (
          <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-medium text-amber-800">
            {teachersError}
          </div>
        )}

        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-500">
            Carregando turmas...
          </div>
        ) : (
          <ClassCatalog
            items={filteredClasses}
            selectedClassId={null}
            onSelect={handleSelectClass}
            onEdit={handleEditClass}
            onRemove={handleRequestRemoveClass}
            onOpenReport={handleOpenClassReport}
          />
        )}

      </div>

      <FullScreenModal
        open={isFormModalOpen}
        onClose={closeFormModal}
        title={editingClassId ? 'Editar turma' : 'Cadastrar turma'}
      >
        <ClassManagementPanel
          mode={editingClassId ? 'edit' : 'create'}
          values={formValues}
          teacherOptions={teacherOptions}
          segmentOptions={segmentOptions}
          onChange={handleChangeFormValue}
          onSubmit={handleSaveClass}
          errorMessage={formError}
        />
      </FullScreenModal>

      <FullScreenModal
        open={classToRemove !== null}
        onClose={() => setClassToRemove(null)}
        title="Remover turma"
        subtitle={classToRemove?.title}
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" className="h-12 border border-gray-200" onClick={() => setClassToRemove(null)}>
              Cancelar
            </Button>
            <Button className="h-12 bg-rose-600 hover:bg-rose-700" onClick={handleConfirmRemoveClass}>
              Remover turma
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
          <AlertTriangle size={20} className="shrink-0 text-rose-600" />
          <p className="text-sm text-rose-900">
            Esta ação remove a turma da listagem atual quando ela não possui vínculos ativos.
          </p>
        </div>
        {removeError && (
          <div className="mt-3 rounded-2xl border border-rose-100 bg-white p-3 text-sm font-medium text-rose-700">
            {removeError}
          </div>
        )}
      </FullScreenModal>
    </MainLayout>
  );
};

export default ClassesPage;
